import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { FileSpreadsheet, FileType2, Trash2, Check } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";

/**
 * Post-creation upload flow: bulk-upload CSV / XLSX / Word questions
 * scoped to a specific assessment. Trainers land here after opting in
 * to "Upload questions after creating this test" in CreateTest.jsx.
 */
export default function AssessmentQuestionsUpload() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [a, qs] = await Promise.all([
        api.get(`/assessments/${id}`),
        api.get(`/assessments/${id}/questions`),
      ]);
      setAssessment(a.data);
      setQuestions(qs.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load assessment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const name = file.name.toLowerCase();
    const ok = [".csv", ".xlsx", ".docx"].some(ext => name.endsWith(ext));
    if (!ok) {
      toast.error("Please pick a .csv, .xlsx or .docx file");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setImporting(true);
    try {
      const { data } = await api.post(`/assessments/${id}/questions/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Added ${data.inserted} question${data.inserted === 1 ? "" : "s"} to this assessment` +
          (data.skipped ? ` · ${data.skipped} skipped` : "")
      );
      if (data.errors?.length) {
        toast.message(`${data.errors.length} rows had issues — first: ${data.errors[0]}`);
      }
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Import failed";
      toast.error(msg);
      if (detail?.hint) toast.message(detail.hint);
    } finally {
      setImporting(false);
    }
  };

  const removeQuestion = async (qid) => {
    if (!window.confirm("Remove this question from the assessment?")) return;
    try {
      await api.delete(`/assessments/${id}/questions/${qid}`);
      setQuestions(qs => qs.filter(q => q.id !== qid));
      toast.success("Question removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    }
  };

  if (loading) {
    return <div className="text-slate-500 text-sm py-10">Loading assessment…</div>;
  }
  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        {typeof error === "string" ? error : "Error loading assessment"}
      </div>
    );
  }

  const totalQs = questions.length;

  return (
    <div>
      <PageHeader
        title="Upload questions"
        subtitle={
          assessment
            ? `Attach questions to “${assessment.title}” — CSV, XLSX, or Word (.docx).`
            : "Attach questions to this assessment."
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upload panel */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="text-sm font-semibold text-slate-900 mb-3">Bulk upload</div>
            <p className="text-xs text-slate-600 mb-4 leading-relaxed">
              Every row/question you upload here is attached ONLY to this assessment. Questions from
              your Question Bank are not affected.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.docx"
              onChange={onFilePick}
              className="hidden"
              data-testid="aqu-file-input"
            />
            <button
              type="button"
              disabled={importing}
              onClick={() => fileRef.current?.click()}
              data-testid="aqu-upload-btn"
              className="w-full bg-blue-600 text-white rounded-lg px-4 py-2.5 font-medium hover:bg-blue-700 disabled:opacity-60"
            >
              {importing ? "Uploading…" : "Choose file to upload"}
            </button>

            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-xs font-semibold text-slate-700 mb-2">Templates</div>
              <div className="flex flex-col gap-2">
                <a
                  href={`${API_BASE}/questions/template`}
                  data-testid="aqu-csv-template"
                  className="flex items-center gap-2 text-xs text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-lg px-3 py-2"
                >
                  <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> CSV / XLSX template
                </a>
                <a
                  href={`${API_BASE}/questions/word-template`}
                  data-testid="aqu-word-template"
                  className="flex items-center gap-2 text-xs text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-lg px-3 py-2"
                >
                  <FileType2 className="w-4 h-4 text-indigo-600" /> Word (.docx) template
                </a>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="text-sm font-semibold text-slate-900 mb-1">Attached questions</div>
            <div className="text-2xl font-semibold text-blue-700">{totalQs}</div>
            <p className="text-xs text-slate-500 mt-1">
              These questions will appear in this assessment for every eligible employee.
            </p>
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => navigate("/trainer/live-exams")}
                data-testid="aqu-done-btn"
                className="flex-1 bg-slate-900 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-slate-800"
              >
                <Check className="w-4 h-4 inline mr-1" /> Done
              </button>
              <button
                type="button"
                onClick={() => navigate("/trainer/create-test")}
                className="border border-slate-200 rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Back
              </button>
            </div>
          </div>
        </div>

        {/* Questions list */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="text-sm font-semibold text-slate-900 mb-3">
              Questions in this assessment
            </div>
            {totalQs === 0 ? (
              <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">
                No questions yet. Upload a CSV, XLSX, or Word file to get started.
              </div>
            ) : (
              <ol
                data-testid="aqu-question-list"
                className="space-y-2 max-h-[70vh] overflow-y-auto pr-1"
              >
                {questions.map((q, i) => (
                  <li
                    key={q.id}
                    data-testid={`aqu-question-${q.id}`}
                    className="border border-slate-200 rounded-lg p-3 flex items-start gap-3"
                  >
                    <div className="text-xs font-semibold text-slate-500 w-6 pt-0.5">{i + 1}.</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 break-words">{q.question}</div>
                      {q.options?.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5">
                          {q.options.map((opt, oi) => (
                            <li
                              key={oi}
                              className={`text-xs ${
                                oi + 1 === q.correct_answer
                                  ? "text-emerald-700 font-medium"
                                  : "text-slate-600"
                              }`}
                            >
                              {String.fromCharCode(65 + oi)}. {opt}
                              {oi + 1 === q.correct_answer && (
                                <Check className="w-3 h-3 inline ml-1" />
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeQuestion(q.id)}
                      data-testid={`aqu-remove-${q.id}`}
                      title="Remove from assessment"
                      className="text-slate-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
